/**
 * Scroll Reveal — Oura-style fade-in micro animations
 *
 * Usage:
 *   data-reveal                 → element fades/slides up when entering viewport
 *   data-reveal-stagger         → children animate in sequence (staggered)
 *   data-reveal-delay="200"     → explicit delay in ms (overrides stagger calc)
 *   data-reveal-direction="left|right|up|down"  → slide direction (default: up)
 */

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const TRANSITION_DURATION = 750;
const STAGGER_DELAY = 100;
const TRANSLATE_AMOUNT = '16px';
const THRESHOLD = 0.12;

/**
 * Apply the hidden (pre-reveal) state to an element
 * @param {HTMLElement} el
 * @param {string} direction
 */
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const TRANSITION_VALUE = `opacity ${TRANSITION_DURATION}ms ${EASING}, transform ${TRANSITION_DURATION}ms ${EASING}`;

function applyHiddenState(el, direction = 'up') {
  const transforms = {
    up: `translateY(${TRANSLATE_AMOUNT})`,
    down: `translateY(-${TRANSLATE_AMOUNT})`,
    left: `translateX(${TRANSLATE_AMOUNT})`,
    right: `translateX(-${TRANSLATE_AMOUNT})`,
  };

  el.style.opacity = '0';
  el.style.transform = transforms[direction] ?? transforms.up;
  el.style.willChange = 'opacity, transform';
}

/**
 * Reveal an element (transition to visible state)
 * @param {HTMLElement} el
 * @param {number} delay
 */
function revealElement(el, delay = 0) {
  const targetOpacity = el.dataset.revealOpacity ?? '1';
  const reveal = () => {
    el.style.opacity = targetOpacity;
    el.style.transform = 'translate3d(0,0,0)';

    const cleanup = () => {
      el.style.willChange = '';
      el.style.transition = '';
      el.style.transform = '';
    };

    el.addEventListener('transitionend', cleanup, { once: true });
  };

  if (delay > 0) {
    setTimeout(reveal, delay);
  } else {
    reveal();
  }
}

class ScrollReveal {
  constructor() {
    if (prefersReducedMotion) return;

    this.observer = new IntersectionObserver(this.#onIntersect.bind(this), {
      threshold: THRESHOLD,
      rootMargin: '0px 0px -40px 0px',
    });

    this.#init();
  }

  #init() {
    /** @type {HTMLElement[]} */
    const elements = [];

    // 1. Apply hidden state (no transition) to all elements
    document.querySelectorAll('[data-reveal]').forEach((el) => {
      // Skip if a parent already has data-reveal (prevents double-animate in nested reveals)
      if (el.parentElement?.closest('[data-reveal]')) return;
      const direction = /** @type {HTMLElement} */ (el).dataset.revealDirection ?? 'up';
      applyHiddenState(/** @type {HTMLElement} */ (el), direction);
      elements.push(/** @type {HTMLElement} */ (el));
    });

    document.querySelectorAll('[data-reveal-stagger]').forEach((container) => {
      // Skip if this stagger container is nested inside a data-reveal element (it would be invisible)
      if (container.parentElement?.closest('[data-reveal]')) return;

      const children = [...container.children].filter(
        (child) =>
          !child.hasAttribute('data-reveal-skip') &&
          !child.hasAttribute('data-reveal')
      );

      children.forEach((child, index) => {
        // If child contains a nested stagger, skip it (it handles its own reveals)
        if (child.querySelector('[data-reveal-stagger]') || child.hasAttribute('data-reveal-stagger')) return;
        child.dataset.revealDelay = child.dataset.revealDelay ?? String(index * STAGGER_DELAY);
        const direction = child.dataset.revealDirection ?? 'up';
        applyHiddenState(/** @type {HTMLElement} */ (child), direction);
        elements.push(/** @type {HTMLElement} */ (child));
      });
    });

    // 2. Wait for browser to paint the hidden state, then add transitions + start observing
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        for (const el of elements) {
          el.style.transition = TRANSITION_VALUE;
        }
        for (const el of elements) {
          this.observer.observe(el);
        }
      });
    });
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onIntersect(entries) {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      const el = /** @type {HTMLElement} */ (entry.target);
      const delay = parseInt(el.dataset.revealDelay ?? '0', 10);
      revealElement(el, delay);
      this.observer.unobserve(el);
    }
  }

  /**
   * Re-scan the DOM for new elements (useful after dynamic content load)
   */
  refresh() {
    this.#init();
  }
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.ScrollReveal = new ScrollReveal();
  });
} else {
  window.ScrollReveal = new ScrollReveal();
}
